/**
 * DeadlinesTab — Firm Portal tab. Firm owner adds application
 * deadlines (grad-scheme, spring insight, open days). Each row has
 * title, kind, deadline_at, apply URL and sectors.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Loader2, Plus, Trash2, ExternalLink, Sparkles, BarChart3, Layers } from "lucide-react";
import CityAutocomplete from "@/components/app/CityAutocomplete";
import UniversityAutocomplete from "@/components/app/UniversityAutocomplete";


const KIND_LABEL = {
  // Application deadlines
  grad_scheme: "Grad scheme",
  internship: "Internship",
  spring_insight: "Spring insight",
  open_day: "Open day",
  hackathon: "Hackathon",
  scholarship: "Scholarship",
  // Events (added Feb 2026)
  drinks_canapes: "Drinks & canapés",
  meet_and_greet: "Meet & greet",
  presentation: "Presentation",
  panel_discussion: "Panel discussion",
  coffee_chat: "Coffee & conversation",
  competition: "Competition",
  workshop: "Workshop",
  summit: "Summit",
  trading_simulation: "Trading simulation",
  careers_fair: "Careers fair",
  alumni_networking: "Alumni networking",
  case_study_night: "Case-study night",
  insight_day: "Insight day",
};


function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}


export default function DeadlinesTab({ firm }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "", kind: "grad_scheme", deadline_at: "",
    apply_url: "", description: "", sectors: "", city: "",
  });
  const [uniObj, setUniObj] = useState(null); // { id, name } | null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/firm-portal/${firm.slug}/deadlines`);
      const items = data.items || [];
      // Batch-fetch RSVP counts in parallel so firms see attendance
      // at a glance for every event they've posted.
      const counts = await Promise.all(
        items.map((r) =>
          api.get(`/deadlines/${r.id}/rsvp-count`)
            .then((res) => res.data?.count ?? 0)
            .catch(() => 0),
        ),
      );
      setRows(items.map((r, i) => ({ ...r, rsvp_count: counts[i] })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load deadlines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [firm.slug]);

  const save = async () => {
    if (!form.title.trim() || !form.deadline_at) {
      toast.error("Title and deadline date are required");
      return;
    }
    setSaving(true);
    try {
      const sectors = form.sectors.split(",").map(s => s.trim()).filter(Boolean);
      await api.post(`/firm-portal/${firm.slug}/deadlines`, {
        title: form.title.trim(),
        kind: form.kind,
        deadline_at: form.deadline_at,
        apply_url: form.apply_url.trim() || null,
        city: form.city.trim() || null,
        university_id: uniObj?.id || null,
        sectors,
        description: form.description.trim(),
      });
      toast.success("Deadline added");
      setForm({ title: "", kind: "grad_scheme", deadline_at: "", apply_url: "", description: "", sectors: "", city: "" });
      setUniObj(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/firm-portal/${firm.slug}/deadlines/${id}`);
      setRows((r) => r.filter(x => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [boostingId, setBoostingId] = useState(null);
  const [analytics, setAnalytics] = useState({}); // { deadline_id: {total, since_boost} }
  const [expanded, setExpanded] = useState(null); // deadline_id whose analytics are open
  const [bundle, setBundle] = useState({ open: false, size: 3, picks: [], submitting: false });
  const boost = async (r) => {
    if (!r.university_id) {
      toast.error("Pin this event to a university first — boost is per-university");
      return;
    }
    setBoostingId(r.id);
    try {
      const { data } = await api.post(
        `/firm-portal/${firm.slug}/deadlines/${r.id}/boost/checkout`,
        { firm_slug: firm.slug, deadline_id: r.id, origin_url: window.location.origin },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.error || d?.message || "Checkout failed";
      toast.error(msg);
      setBoostingId(null);
    }
  };

  // If we came back from Stripe with a `boost_session_id` in the URL,
  // poll the status endpoint until it flips to paid then reload rows.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("boost_session_id");
    if (!sid) return;
    if (params.get("boost_cancelled")) {
      toast.info("Boost cancelled");
      const url = new URL(window.location.href);
      url.searchParams.delete("boost_session_id");
      url.searchParams.delete("boost_cancelled");
      window.history.replaceState({}, "", url.toString());
      return;
    }
    let stopped = false;
    (async () => {
      for (let i = 0; i < 8 && !stopped; i++) {
        try {
          const { data } = await api.get(
            `/firm-portal/${firm.slug}/deadlines/boost/status/${sid}`,
          );
          if (data.payment_status === "paid") {
            toast.success("Event boosted — it's now top of the feed");
            const url = new URL(window.location.href);
            url.searchParams.delete("boost_session_id");
            window.history.replaceState({}, "", url.toString());
            load();
            return;
          }
        } catch { /* silent — will retry */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
    })();
    return () => { stopped = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm.slug]);

  const loadAnalytics = async (id) => {
    try {
      const { data } = await api.get(
        `/firm-portal/${firm.slug}/deadlines/${id}/analytics`,
      );
      setAnalytics((a) => ({ ...a, [id]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load analytics");
    }
  };

  const toggleAnalytics = (id) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!analytics[id]) loadAnalytics(id);
  };

  const openBundle = (size) => {
    setBundle({ open: true, size, picks: [], submitting: false });
  };

  const togglePick = (id) => {
    setBundle((b) => {
      if (b.picks.includes(id)) {
        return { ...b, picks: b.picks.filter(x => x !== id) };
      }
      if (b.picks.length >= b.size) return b;
      return { ...b, picks: [...b.picks, id] };
    });
  };

  const sendBundle = async () => {
    if (bundle.picks.length !== bundle.size) {
      toast.error(`Pick exactly ${bundle.size} events`);
      return;
    }
    setBundle((b) => ({ ...b, submitting: true }));
    try {
      const { data } = await api.post(
        `/firm-portal/${firm.slug}/deadlines/boost/bundle-checkout`,
        {
          firm_slug: firm.slug,
          deadline_ids: bundle.picks,
          origin_url: window.location.origin,
        },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.error || d?.message || "Bundle checkout failed";
      toast.error(msg);
      setBundle((b) => ({ ...b, submitting: false }));
    }
  };

  // Bundle eligibility: pinned to uni, not already boosted.
  const bundleEligible = (r) => r.university_id && !r.sponsored_paid_at;
  const eligibleCount = rows.filter(bundleEligible).length;

  return (
    <div className="space-y-5" data-testid="firm-portal-deadlines-tab">
      <div className="p-4 rounded-xl border border-[#4C1D95]/25 bg-[#F5F0FF]/50">
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <CalendarClock className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">Deadline feed</span>
        </div>
        <p className="mt-1 text-sm text-stone-700">
          Add your grad-scheme, internship windows and campus events
          once. They land on the student's Journey Rail for anyone with
          a mutual match, the sector-filtered <code>/deadlines</code>
          browse, and — when you pin an event to a university — a
          teaser card on that <code>/uni/&lt;id&gt;</code> page. Students
          pinning a deadline get 14/3/1-day email reminders automatically.
        </p>
      </div>

      <section
        className="p-4 rounded-xl border border-stone-200 bg-white space-y-3"
        data-testid="firm-portal-deadlines-form"
      >
        <div className="grid sm:grid-cols-2 gap-2">
          <Input
            placeholder="Title (e.g. Analyst Grad Scheme 2027)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="firm-portal-deadline-title"
          />
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="h-10 rounded-md border border-stone-300 bg-white px-2 text-sm"
            data-testid="firm-portal-deadline-kind"
          >
            <optgroup label="Application deadlines">
              {["grad_scheme", "internship", "spring_insight", "open_day", "hackathon", "scholarship"].map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k]}</option>
              ))}
            </optgroup>
            <optgroup label="Campus events">
              {["drinks_canapes", "meet_and_greet", "presentation", "panel_discussion",
                "coffee_chat", "competition", "workshop", "summit", "trading_simulation",
                "careers_fair", "alumni_networking", "case_study_night", "insight_day"].map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k]}</option>
              ))}
            </optgroup>
          </select>
          <Input
            type="date"
            value={form.deadline_at}
            onChange={(e) => setForm({ ...form, deadline_at: e.target.value })}
            data-testid="firm-portal-deadline-date"
          />
          <Input
            placeholder="Apply URL (https://…)"
            value={form.apply_url}
            onChange={(e) => setForm({ ...form, apply_url: e.target.value })}
            data-testid="firm-portal-deadline-url"
          />
        </div>
        <Input
          placeholder="Sectors (comma-separated: finance, consulting)"
          value={form.sectors}
          onChange={(e) => setForm({ ...form, sectors: e.target.value })}
          data-testid="firm-portal-deadline-sectors"
        />
        <CityAutocomplete
          value={form.city}
          onChange={(v) => setForm({ ...form, city: v })}
          placeholder="City (optional — e.g. London, New York)"
          maxLength={80}
          testIdPrefix="firm-portal-deadline-city"
        />
        <div data-testid="firm-portal-deadline-uni-wrap">
          <UniversityAutocomplete
            value={uniObj}
            onSelect={(u) => setUniObj(u || null)}
            placeholder="Pin to a university (optional — surfaces a teaser on /uni/…)"
            testIdPrefix="firm-portal-deadline-uni"
          />
        </div>
        <Textarea
          rows={2}
          placeholder="1–2 line description shown on the card"
          maxLength={400}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="firm-portal-deadline-desc"
        />
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
          data-testid="firm-portal-deadline-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          Add deadline
        </Button>
      </section>

      <section data-testid="firm-portal-deadlines-list">
        <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
            Your deadlines ({rows.length})
          </div>
          {eligibleCount >= 3 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openBundle(3)}
                data-testid="firm-portal-deadline-bundle-3-open"
                className="border-amber-400 text-amber-900 hover:bg-amber-50 h-8 text-[11px]"
                title="Boost 3 events at 12% off (£129)"
              >
                <Layers className="h-3 w-3 mr-1" /> 3-uni · £129
              </Button>
              {eligibleCount >= 5 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBundle(5)}
                  data-testid="firm-portal-deadline-bundle-5-open"
                  className="border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100 h-8 text-[11px]"
                  title="Boost 5 events at 19% off (£199)"
                >
                  <Layers className="h-3 w-3 mr-1" /> 5-uni · £199
                </Button>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <div className="text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-stone-300 rounded-xl text-stone-500 text-sm">
            No deadlines yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li
                key={r.id}
                className="p-3 rounded-xl border border-stone-200 bg-white"
                data-testid={`firm-portal-deadline-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-stone-900 text-sm">{r.title}</span>
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5">{KIND_LABEL[r.kind] || r.kind}</Badge>
                    {(r.sectors || []).map((s) => (
                      <Badge key={s} className="text-[10px] py-0 px-1.5 bg-stone-100 text-stone-700 border border-stone-200">{s}</Badge>
                    ))}
                    {r.sponsored_paid_at && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2 py-0.5"
                        data-testid={`firm-portal-deadline-sponsored-${r.id}`}
                        title="Boosted — top of the university feed"
                      >
                        <Sparkles className="h-2.5 w-2.5" /> Sponsored
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-stone-500">
                    Closes {fmt(r.deadline_at)}
                    {r.city && <> · {r.city}</>}
                    {r.university_name && (
                      <> · <span className="text-[#4C1D95]">{r.university_name}</span></>
                    )}
                    {r.apply_url && (
                      <> · <a href={r.apply_url} target="_blank" rel="noopener noreferrer" className="text-[#4C1D95] underline">apply</a></>
                    )}
                    {typeof r.rsvp_count === "number" && r.rsvp_count > 0 && (
                      <> · <span
                        className="inline-flex items-center gap-0.5 text-emerald-800 font-medium"
                        data-testid={`firm-portal-deadline-rsvp-count-${r.id}`}
                        title="Students who RSVP'd"
                      >
                        {r.rsvp_count} going
                      </span></>
                    )}
                  </div>
                  {r.description && <p className="mt-1 text-[12px] text-stone-600">{r.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleAnalytics(r.id)}
                    data-testid={`firm-portal-deadline-analytics-${r.id}`}
                    className="h-8 text-[11px]"
                    title="Impressions · clicks"
                  >
                    <BarChart3 className="h-3.5 w-3.5" />
                  </Button>
                  {r.university_id && !r.sponsored_paid_at && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => boost(r)}
                      disabled={boostingId === r.id}
                      data-testid={`firm-portal-deadline-boost-${r.id}`}
                      className="border-amber-400 text-amber-800 hover:bg-amber-50 h-8 text-[11px]"
                      title="Boost to the top of this university's feed — £49 one-off"
                    >
                      {boostingId === r.id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <><Sparkles className="h-3 w-3 mr-1" /> Boost £49</>}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(r.id)}
                    data-testid={`firm-portal-deadline-delete-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                </div>
                {expanded === r.id && (
                  <div
                    className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-3 gap-3 text-[12px]"
                    data-testid={`firm-portal-deadline-analytics-strip-${r.id}`}
                  >
                    {!analytics[r.id] ? (
                      <div className="col-span-3 text-stone-500 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[9px] font-underground-sc tracking-widest text-stone-500">Total impressions</div>
                          <div className="mt-0.5 font-medium text-stone-900 text-base">{analytics[r.id].total.impressions.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[9px] font-underground-sc tracking-widest text-stone-500">Total clicks</div>
                          <div className="mt-0.5 font-medium text-stone-900 text-base">{analytics[r.id].total.clicks.toLocaleString()}</div>
                          <div className="text-[10px] font-mono text-stone-500">CTR {(analytics[r.id].total.ctr * 100).toFixed(1)}%</div>
                        </div>
                        <div>
                          {analytics[r.id].since_boost ? (
                            <>
                              <div className="text-[9px] font-underground-sc tracking-widest text-amber-900">Since boost</div>
                              <div className="mt-0.5 font-medium text-stone-900 text-base">
                                {analytics[r.id].since_boost.impressions.toLocaleString()} <span className="text-[10px] font-mono text-stone-500">imp</span>
                                <span className="mx-1 text-stone-400">·</span>
                                {analytics[r.id].since_boost.clicks.toLocaleString()} <span className="text-[10px] font-mono text-stone-500">clk</span>
                              </div>
                              <div className="text-[10px] font-mono text-amber-800">Boosted {new Date(analytics[r.id].sponsored_paid_at).toLocaleDateString()}</div>
                            </>
                          ) : (
                            <div className="text-[10px] font-mono text-stone-500 italic self-center">
                              Not boosted yet — boost to compare lift
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Bundle picker modal */}
      {bundle.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="firm-portal-deadline-bundle-modal"
          onClick={() => !bundle.submitting && setBundle({ ...bundle, open: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white rounded-2xl border border-stone-200 shadow-xl p-5 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-900">
                <Layers className="h-4 w-4" />
                <span className="text-[10px] font-underground-sc tracking-widest">
                  {bundle.size}-uni boost bundle · {bundle.size === 3 ? "£129 (12% off)" : "£199 (19% off)"}
                </span>
              </div>
              <button
                onClick={() => setBundle({ ...bundle, open: false })}
                disabled={bundle.submitting}
                className="text-stone-400 hover:text-stone-700 text-2xl leading-none"
                aria-label="Close"
              >×</button>
            </div>
            <p className="text-[13px] text-stone-600 leading-relaxed">
              Pick <span className="font-medium text-stone-900">exactly {bundle.size}</span> events to boost together. Each must be pinned to a university and not already boosted. All {bundle.size} boosts land in one Stripe payment.
            </p>
            <div className="text-[10px] font-mono text-stone-500">
              {bundle.picks.length}/{bundle.size} selected
            </div>
            <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {rows.filter(bundleEligible).map((r) => {
                const on = bundle.picks.includes(r.id);
                const disabled = !on && bundle.picks.length >= bundle.size;
                return (
                  <li key={r.id}>
                    <label
                      className={
                        "flex items-start gap-2 p-2 rounded-md border cursor-pointer transition " +
                        (on
                          ? "border-amber-400 bg-amber-50"
                          : disabled
                            ? "border-stone-200 bg-stone-50 opacity-50 cursor-not-allowed"
                            : "border-stone-200 hover:border-amber-300 hover:bg-amber-50/50")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={disabled}
                        onChange={() => togglePick(r.id)}
                        data-testid={`firm-portal-deadline-bundle-pick-${r.id}`}
                        className="mt-1"
                      />
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-stone-900 truncate">{r.title}</div>
                        <div className="text-[11px] font-mono text-stone-500 truncate">
                          {KIND_LABEL[r.kind] || r.kind} · {r.university_name} · closes {fmt(r.deadline_at)}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
              <Button
                variant="ghost"
                onClick={() => setBundle({ ...bundle, open: false })}
                disabled={bundle.submitting}
              >Cancel</Button>
              <Button
                onClick={sendBundle}
                disabled={bundle.submitting || bundle.picks.length !== bundle.size}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="firm-portal-deadline-bundle-send"
              >
                {bundle.submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Boost {bundle.size} events
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
